/**
 * Popup display frequency control
 * Controls the display frequency of individual popups based on configuration
 */
(function () {
  // Private helper methods
  const _getPopupRecord = (popupId) => {
    try {
      const key = "_erj_popup_closed_" + popupId;
      const recordStr = localStorage.getItem(key);
      if (recordStr) {
        return JSON.parse(recordStr);
      }
    } catch (e) {}
    return null;
  };

  const _updatePopupRecord = (popupId, data, configSettings) => {
    try {
      const key = "_erj_popup_closed_" + popupId;
      // Add configuration info to record data
      const recordData = {
        ...data,
        config: configSettings
          ? {
              periodDays: parseInt(
                configSettings.frequency_period_days ||
                  configSettings.frequency_days ||
                  7,
                10
              ),
              maxViews: parseInt(configSettings.frequency_max_views || 0, 10),
            }
          : undefined,
      };
      localStorage.setItem(key, JSON.stringify(recordData));
      return true;
    } catch (e) {
      return false;
    }
  };

  const _calculatePeriodStart = (periodDays) => {
    const now = new Date();
    const dayMs = 24 * 60 * 60 * 1000;
    if (periodDays <= 1) {
      const dayStart = new Date(now);
      dayStart.setHours(0, 0, 0, 0);
      return dayStart.getTime();
    } else {
      const periodMs = periodDays * dayMs;
      const periodStart = Math.floor(now.getTime() / periodMs) * periodMs;
      return periodStart;
    }
  };

  const _isInSamePeriod = (recordTimestamp, currentPeriodStart) => {
    return recordTimestamp >= currentPeriodStart;
  };

  const _canShowPeriodicPopup = (popupId, maxViews, periodDays, settings) => {
    if (maxViews <= 0) return true;

    const currentPeriodStart = _calculatePeriodStart(periodDays);
    const record = _getPopupRecord(popupId);

    if (!record) return true;

    // Check if configuration has changed
    const configChanged =
      !record.config ||
      record.config.periodDays !== periodDays ||
      record.config.maxViews !== maxViews;

    // If config changed or not in the same period, reset the record
    if (
      configChanged ||
      !_isInSamePeriod(record.periodStartTime, currentPeriodStart)
    ) {
      _updatePopupRecord(
        popupId,
        {
          count: 0,
          lastClosedTime: 0,
          periodStartTime: currentPeriodStart,
        },
        settings
      );
      return true;
    }

    return record.count < maxViews;
  };

  // Public API
  const PopupFrequency = {
    /**
     * Check if a popup can be shown based on local frequency control settings
     * This is only used for local frequency limitations (non-server based)
     * @param {String} popupId Popup ID
     * @param {Object} settings Popup settings
     * @return {boolean} Whether the popup can be shown based on local frequency settings
     */
    checkLocalFrequencyLimit(popupId, settings) {
      if (!popupId || !settings) {
        return true;
      }

      const timeRangeType = settings.frequency_type ?? "";

      switch (timeRangeType) {
        case "always_show":
          return true;
        case "once":
          // For "once" type, check if popup has been shown before
          const record = _getPopupRecord(popupId);
          // If no record exists, it has never been shown before
          return !record || !record.lastClosedTime;
        case "periodic_display": {
          const maxViews = parseInt(settings.frequency_max_views, 10) || 0;
          const periodDays =
            parseInt(
              settings.frequency_period_days || settings.frequency_days,
              10
            ) || 7;
          return _canShowPeriodicPopup(popupId, maxViews, periodDays, settings);
        }
        default:
          return true;
      }
    },

    recordPopup(popupId, settings) {
      if (!popupId || !settings) return false;

      const timeRangeType = settings?.frequency_type ?? "";

      switch (timeRangeType) {
        case "always_show":
          return true;
        case "once": {
          // For once type, just record the closed timestamp
          const now = Date.now();
          return _updatePopupRecord(
            popupId,
            {
              count: 1,
              lastClosedTime: now,
              periodStartTime: now,
            },
            settings
          );
        }
        case "periodic_display": {
          const periodDays =
            parseInt(
              settings?.frequency_period_days || settings?.frequency_days,
              10
            ) || 7;
          const currentPeriodStart = _calculatePeriodStart(periodDays);
          const record = _getPopupRecord(popupId) || {
            count: 0,
            lastClosedTime: 0,
            periodStartTime: currentPeriodStart,
          };

          const now = Date.now();
          const newRecord = {
            count: record.count + 1,
            lastClosedTime: now,
            periodStartTime: record.periodStartTime,
          };

          if (!_isInSamePeriod(record.periodStartTime, currentPeriodStart)) {
            newRecord.count = 1;
            newRecord.periodStartTime = currentPeriodStart;
          }

          // Update record with latest configuration
          return _updatePopupRecord(popupId, newRecord, settings);
        }
        default:
          return true;
      }
    },

    /**
     * Master method to determine if a popup should be displayed
     * Combines login status check and popup type-specific rules
     * @param {String} popupId Popup ID
     * @param {String} popupType Popup type (e.g., 'newsletter')
     * @param {Object} settings Popup settings
     * @return {Promise} Promise that resolves to true if popup should be displayed, false otherwise
     */
    determinePopupDisplay(popupId, popupType, settings) {
      return new Promise((resolve, reject) => {
        // Check if user is logged in
        const cid = window._erj_?.customer?.id;
        const email = window._erj_?.customer?.email;

        // If user is not logged in, use local frequency control
        if (!cid) {
          resolve(this.checkLocalFrequencyLimit(popupId, settings));
          return;
        }

        // For logged-in users, handle based on popup type
        switch (popupType) {
          case "newsletter":
            // For newsletter type, use API to determine display
            const controller = new AbortController();
            const timeoutId = setTimeout(() => {
              controller.abort();
            }, 20 * 1000); // 20 seconds timeout

            fetch("https://api.enroutejewelry.com/should_show_popup", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                email: email,
              }),
              mode: "cors",
              signal: controller.signal,
            })
              .then((response) => response.json())
              .then((result) => {
                clearTimeout(timeoutId);
                if (!result || !result.data) {
                  resolve(false);
                  return;
                }

                resolve(true);
              })
              .catch((error) => {
                clearTimeout(timeoutId);
                resolve(false);
              });
            break;

          default:
            // For other popup types, use local frequency control
            resolve(this.checkLocalFrequencyLimit(popupId, settings));
            break;
        }
      });
    },
  };

  // Expose to global
  window._erj_ = window._erj_ || {};
  window._erj_.PopupFrequency = PopupFrequency;
})();
